import { api, ApiError } from './api.js';
import { gameState } from '../state/gameState.js';
import { MOBILE_LAYOUT_QUERY } from '../utils/mobile-layout.js';

interface NpcMessageView {
  id: string;
  npcName: string;
  text: string;
}

let initialized = false;
let pollTimer: ReturnType<typeof setInterval> | null = null;
let requestInFlight = false;

const POLL_INTERVAL_MS = 20_000;
const MODAL_ID = 'global-npc-message-modal';
const STYLE_ID = 'global-npc-message-modal-style';

function ensureStyles(): void {
  if (document.getElementById(STYLE_ID)) return;

  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    .gnpc-backdrop {
      position: fixed;
      inset: 0;
      z-index: 2000;
      background: color-mix(in srgb, var(--bg-base) 86%, transparent);
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 16px;
    }
    .gnpc-modal {
      width: min(560px, calc(100vw - 24px));
      border: 1px solid var(--border);
      border-radius: var(--radius);
      background: var(--bg-elevated);
      padding: 16px;
      font-family: var(--font-mono);
      color: var(--text-primary);
    }
    .gnpc-title {
      margin: 0 0 12px 0;
      font-size: var(--font-size-sm);
      letter-spacing: 0.08em;
      text-transform: uppercase;
    }
    .gnpc-sender {
      margin: 0;
      font-size: var(--font-size-sm);
      letter-spacing: 0.04em;
      text-transform: uppercase;
    }
    .gnpc-text {
      margin: 12px 0 0 0;
      font-size: var(--font-size-xs);
      line-height: 1.45;
      color: var(--text-primary);
    }
    .gnpc-actions {
      margin-top: 14px;
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 8px;
    }
    .gnpc-btn {
      min-height: 44px;
      border: 1px solid var(--border);
      border-radius: var(--radius-btn);
      background: var(--bg-surface);
      color: var(--text-primary);
      font-family: var(--font-mono);
      font-size: var(--font-size-xs);
      letter-spacing: 0.08em;
      text-transform: uppercase;
      cursor: pointer;
    }
    .gnpc-btn:disabled {
      opacity: 0.6;
      cursor: default;
    }
    .gnpc-btn--primary {
      border-color: color-mix(in srgb, var(--accent-gold) 42%, transparent);
      color: var(--accent-gold);
    }
    @media ${MOBILE_LAYOUT_QUERY} {
      .gnpc-actions {
        grid-template-columns: 1fr;
      }
    }
  `;

  document.head.appendChild(style);
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function closeModal(): void {
  document.getElementById(MODAL_ID)?.remove();
}

async function handleDecision(messageId: string, action: 'accept' | 'ignore'): Promise<void> {
  const modal = document.getElementById(MODAL_ID);
  if (!modal) return;

  const buttons = modal.querySelectorAll<HTMLButtonElement>('.gnpc-btn');
  buttons.forEach((button) => {
    button.disabled = true;
  });

  try {
    await api.post(`/orders/messages/${encodeURIComponent(messageId)}/${action}`, {});
    closeModal();
    await checkForNpcMessages();
  } catch (error) {
    buttons.forEach((button) => {
      button.disabled = false;
    });

    if (error instanceof ApiError && error.status === 401) {
      closeModal();
      stopPolling();
    }
  }
}

function renderModal(message: NpcMessageView): void {
  closeModal();

  const modal = document.createElement('div');
  modal.id = MODAL_ID;
  modal.className = 'gnpc-backdrop';
  modal.innerHTML = `
    <div class="gnpc-modal" role="dialog" aria-modal="true" aria-label="Neue Nachricht">
      <h3 class="gnpc-title">NEUE NACHRICHT</h3>
      <p class="gnpc-sender">${escapeHtml(message.npcName.toUpperCase())}</p>
      <p class="gnpc-text">${escapeHtml(message.text)}</p>
      <div class="gnpc-actions">
        <button type="button" class="gnpc-btn gnpc-btn--primary" data-gnpc-action="accept" data-message-id="${escapeHtml(message.id)}">ANNEHMEN</button>
        <button type="button" class="gnpc-btn" data-gnpc-action="ignore" data-message-id="${escapeHtml(message.id)}">IGNORIEREN</button>
      </div>
    </div>
  `;

  modal.addEventListener('click', (event) => {
    const target = event.target as Element;
    const button = target.closest<HTMLButtonElement>('[data-gnpc-action]');
    if (!button) return;

    const action = button.dataset.gnpcAction;
    const messageId = button.dataset.messageId;
    if (!messageId || (action !== 'accept' && action !== 'ignore')) return;

    void handleDecision(messageId, action);
  });

  document.body.appendChild(modal);
}

async function checkForNpcMessages(): Promise<void> {
  if (!gameState.isAuthenticated() || requestInFlight) {
    if (!gameState.isAuthenticated()) closeModal();
    return;
  }

  requestInFlight = true;
  try {
    const response = await api.get<{ messages: NpcMessageView[] }>('/orders/messages');
    const nextMessage = response.messages[0] ?? null;
    if (!nextMessage) {
      closeModal();
      return;
    }

    renderModal(nextMessage);
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      closeModal();
      stopPolling();
    }
  } finally {
    requestInFlight = false;
  }
}

function startPolling(): void {
  if (pollTimer !== null) return;

  void checkForNpcMessages();
  pollTimer = setInterval(() => {
    void checkForNpcMessages();
  }, POLL_INTERVAL_MS);
}

function stopPolling(): void {
  if (pollTimer !== null) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  closeModal();
}

export function initNpcMessageCenter(): void {
  if (initialized) return;
  initialized = true;

  ensureStyles();

  gameState.on('player:updated', () => {
    startPolling();
  });

  gameState.on('auth:logout', () => {
    stopPolling();
  });

  if (gameState.isAuthenticated()) {
    startPolling();
  }
}
